import { useEffect, useState } from "react";

import { api } from "services";

import { HeatSourceRow } from "./aggregations/taxaHeatmap";

const PAGE = 500;
const CAP = 5000;

type Base = Record<string, unknown>;

// Coleta as linhas transacionadas (Concluído, status 6) via /Quote/report —
// projeção leve e rápida, paginando até o fim ou o teto. Considera apenas
// orçamentos com repasse (compensationValue > 0): o status 6 do report inclui
// "finalizados" que nunca foram pagos (comp=0, sem liquidação), que não são
// transação real e poluiriam o mapa. (O paymentDate do report é zerado, por
// isso o sinal de "transacionado de fato" é o repasse.)
async function fetchTransacted(
  base: Base,
  status: number,
  signal: AbortSignal
): Promise<HeatSourceRow[]> {
  let page = 1;
  let fetched = 0;
  const rows: HeatSourceRow[] = [];
  for (;;) {
    const { data } = await api.get("/Quote/report", {
      params: { ...base, status, page, size: PAGE },
      signal,
    });
    const batch: HeatSourceRow[] = Array.isArray(data) ? data : [];
    for (const r of batch) {
      const tv = r?.totalValue ?? 0;
      const comp = r?.compensationValue ?? 0;
      if (tv <= 0 || comp <= 0) continue;
      rows.push({
        parcelQtd: r?.parcelQtd,
        totalValue: tv,
        compensationValue: comp,
        paymentCode: r?.paymentCode,
      });
    }
    fetched += batch.length;
    if (batch.length < PAGE || fetched >= CAP) break;
    page += 1;
  }
  return rows;
}

export function useDashboardData(
  companyId: number,
  professionalId: number,
  startISO: string,
  endISO: string,
  enabled: boolean
) {
  const [heatmapRows, setHeatmapRows] = useState<HeatSourceRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const sig = controller.signal;
    const co = companyId === -1 ? undefined : companyId;
    const doc = professionalId === -1 ? undefined : professionalId;
    const base: Base = {
      onlyOpened: false,
      beginDateCreate: startISO,
      endDateCreate: endISO,
      companyId: co,
      doctorId: doc,
    };

    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        setHeatmapRows(await fetchTransacted(base, 6, sig));
      } catch (e: any) {
        if (e?.code !== "ERR_CANCELED") {
          console.error("[Dashboard] carga falhou", e);
          setError("Não foi possível carregar os dados. Tente atualizar.");
        }
      } finally {
        setIsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [companyId, professionalId, startISO, endISO, enabled]);

  return { heatmapRows, isLoading, error };
}
